import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { employeesAPI } from '@/api/employees';
import { authAPI } from '@/api/auth';
import { showError, showSuccess } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Plus, Trash2, Users, Key, Settings, Shield, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Валидация пароля (зеркало backend validate_password_strength)
const validatePasswordStrength = (password) => {
  if (password.length < 12) return 'Пароль должен быть не менее 12 символов';
  if (!/[A-ZА-ЯЁ]/.test(password)) return 'Нужна хотя бы одна заглавная буква';
  if (!/[a-zа-яё]/.test(password)) return 'Нужна хотя бы одна строчная буква';
  if (!/[0-9]/.test(password)) return 'Нужна хотя бы одна цифра';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(password)) return 'Нужен хотя бы один спецсимвол (!@#$%^&*...)';
  return null;
};

// Stage 9: Роли для сотрудников Bitrix (аналитика)
const EMPLOYEE_ROLE_OPTIONS = [
  { value: 'Admin', label: 'Admin', color: 'bg-red-100 text-red-800' },
  { value: 'Moderator', label: 'Moderator', color: 'bg-blue-100 text-blue-800' },
  { value: 'User', label: 'User', color: 'bg-gray-100 text-gray-800' },
];

// Stage 9: Роли для системных пользователей (4 аккаунта)
const SYSTEM_USER_ROLE_OPTIONS = [
  { value: 'finance_admin', label: 'Finance Admin', color: 'bg-purple-100 text-purple-800', description: 'Полный доступ к финансам и калькуляции' },
  { value: 'senior_manager', label: 'Senior Manager', color: 'bg-green-100 text-green-800', description: 'Доступ к проектам, без финансовых блоков' },
];

// Все роли для матрицы доступа (admin не включён - имеет полный доступ по умолчанию)
const ALL_ROLES = [
  { value: 'finance_admin', label: 'Finance Admin', color: 'bg-purple-100 text-purple-800', description: 'Полный доступ к финансам и калькуляции' },
  { value: 'senior_manager', label: 'Senior Manager', color: 'bg-green-100 text-green-800', description: 'Доступ к проектам, без финансовых блоков' },
];

// Дефолтная матрица доступа (если не загружена из API)
// admin не включён - имеет полный доступ по умолчанию
// Роли: finance_admin, senior_manager
const DEFAULT_PAGE_ACCESS_MATRIX = [
  { page: 'Dashboard', path: '/dashboard', finance_admin: true, senior_manager: true },
  { page: 'Проекты (Overview)', path: '/projects', finance_admin: true, senior_manager: true },
  { page: 'Проекты (Список)', path: '/projects/list', finance_admin: true, senior_manager: true },
  { page: 'Создание проекта', path: '/projects/new', finance_admin: true, senior_manager: true },
  { page: 'Детали проекта', path: '/projects/:id', finance_admin: true, senior_manager: true },
  { page: 'Gantt-диаграмма', path: '/projects/:id/gantt', finance_admin: true, senior_manager: true },
  { page: 'Сотрудники', path: '/employees', finance_admin: true, senior_manager: true },
  { page: 'Конструктор отчётов', path: '/report-builder', finance_admin: true, senior_manager: true },
  { page: 'Bitrix интеграция', path: '/integrations/bitrix', finance_admin: true, senior_manager: false },
  { page: 'Field Registry', path: '/admin/field-registry', finance_admin: true, senior_manager: false },
];

const EmployeesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);  // 4 пользователя для работы с БД
  const [activeTab, setActiveTab] = useState('system-users');  // system-users | bitrix-employees
  const [pageAccessMatrix, setPageAccessMatrix] = useState(DEFAULT_PAGE_ACCESS_MATRIX);  // Матрица доступа
  const [updatingAccess, setUpdatingAccess] = useState(null);  // {page, role} при обновлении
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [error, setError] = useState('');

  // Check user permissions
  const userRole = user?.role?.toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'finance_admin';
  const canCreate = isAdmin;
  const canDelete = userRole === 'admin';  // Только системный admin может удалять
  const canChangeRole = isAdmin;
  const canResetPassword = isAdmin;

  // Form state for create
  const [formData, setFormData] = useState({
    full_name: '',
    position: '',
    email: '',
    role: 'User',
  });

  // Reset password form
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  // Change own password form
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    loadEmployees();
    loadSystemUsers();
    loadPageAccess();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeesAPI.getAll();
      setEmployees(data);
    } catch (error) {
      showError(error, 'Ошибка загрузки сотрудников');
    } finally {
      setLoading(false);
    }
  };

  const loadSystemUsers = async () => {
    try {
      const data = await authAPI.getSystemUsers();
      setSystemUsers(data);
    } catch (error) {
      // Не показываем ошибку если нет прав - просто не загружаем
      console.log('System users not loaded:', error.message);
    }
  };

  const loadPageAccess = async () => {
    try {
      const data = await authAPI.getPageAccess();
      if (data && data.length > 0) {
        setPageAccessMatrix(data);
      }
    } catch (error) {
      // Используем дефолтные значения
      console.log('Page access not loaded, using defaults:', error.message);
    }
  };

  const handlePageAccessToggle = async (page, role) => {
    // Находим текущее значение
    const currentRow = pageAccessMatrix.find(r => r.page === page);
    if (!currentRow) return;

    const currentValue = currentRow[role];
    const newValue = !currentValue;

    // Оптимистичное обновление
    setPageAccessMatrix(prev => prev.map(row =>
      row.page === page ? { ...row, [role]: newValue } : row
    ));
    setUpdatingAccess({ page, role });

    try {
      await authAPI.updatePageAccess(page, role, newValue);
      showSuccess(`Доступ к "${page}" для ${role} ${newValue ? 'включён' : 'выключен'}`);
    } catch (err) {
      // Откат
      setPageAccessMatrix(prev => prev.map(row =>
        row.page === page ? { ...row, [role]: currentValue } : row
      ));
      showError(err, 'Ошибка при изменении доступа');
    } finally {
      setUpdatingAccess(null);
    }
  };

  const handleSystemUserRoleChange = async (userId, newRole) => {
    const oldUser = systemUsers.find(u => u.id === userId);
    const oldRole = oldUser?.role;

    // Оптимистичное обновление
    setSystemUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, role: newRole } : u
    ));
    setUpdatingRoleId(userId);

    try {
      await authAPI.updateSystemUserRole(userId, newRole);
      showSuccess(`Роль изменена на ${newRole}`);
    } catch (err) {
      // Откат
      setSystemUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: oldRole } : u
      ));
      showError(err, 'Ошибка при изменении роли');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name || !formData.email) {
      setError('Заполните обязательные поля');
      return;
    }

    try {
      await employeesAPI.create(formData);
      setShowCreateDialog(false);
      setFormData({ full_name: '', position: '', email: '', role: 'User' });
      showSuccess('Сотрудник создан. Администратор должен сбросить пароль через панель управления.');
      loadEmployees();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ошибка при создании';
      setError(errorMsg);
      showError(err, errorMsg);
    }
  };

  const handleDelete = async () => {
    if (!selectedEmployee) return;

    try {
      await employeesAPI.delete(selectedEmployee.id);
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      showSuccess('Сотрудник удалён');
      loadEmployees();
    } catch (err) {
      const errorMsg = err.response?.status === 409 
        ? 'Невозможно удалить: сотрудник назначен на проект'
        : err.response?.data?.detail || 'Ошибка при удалении';
      showError(err, errorMsg);
      setShowDeleteDialog(false);
    }
  };

  const handleRoleChange = async (employeeId, newRole) => {
    // Сохраняем старую роль для отката
    const employee = employees.find(e => e.id === employeeId);
    const oldRole = employee?.role;

    // Оптимистичное обновление UI
    setEmployees(prev => prev.map(emp => 
      emp.id === employeeId ? { ...emp, role: newRole } : emp
    ));
    setUpdatingRoleId(employeeId);

    try {
      await authAPI.updateRole(employeeId, newRole);
      showSuccess(`Роль изменена на ${newRole}`);
    } catch (err) {
      // Откат при ошибке
      setEmployees(prev => prev.map(emp => 
        emp.id === employeeId ? { ...emp, role: oldRole } : emp
      ));
      showError(err, 'Ошибка при изменении роли');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleResetPassword = async () => {
    // Работает с selectedUser (системный пользователь) или selectedEmployee
    const targetUser = selectedUser || selectedEmployee;
    if (!targetUser) return;

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    const pwdError = validatePasswordStrength(resetPasswordData.newPassword);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    try {
      await authAPI.resetPassword(targetUser.id, resetPasswordData.newPassword);
      setShowResetPasswordDialog(false);
      setResetPasswordData({ newPassword: '', confirmPassword: '' });
      setSelectedUser(null);
      setSelectedEmployee(null);
      showSuccess('Пароль сброшен');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ошибка сброса пароля';
      setError(errorMsg);
      showError(err, errorMsg);
    }
  };

  const handleChangeOwnPassword = async () => {
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setError('Новые пароли не совпадают');
      return;
    }

    const pwdError = validatePasswordStrength(changePasswordData.newPassword);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    try {
      await authAPI.changePassword(
        changePasswordData.currentPassword, 
        changePasswordData.newPassword
      );
      setShowChangePasswordDialog(false);
      setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSuccess('Ваш пароль изменён');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ошибка смены пароля';
      setError(errorMsg);
      showError(err, errorMsg);
    }
  };

  const getRoleBadge = (role) => {
    const option = EMPLOYEE_ROLE_OPTIONS.find(o => o.value === role);
    return option || EMPLOYEE_ROLE_OPTIONS[2]; // Default to User
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="employees-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Управление персоналом
                </h1>
                <p className="text-sm text-gray-600">
                  {employees.length} сотрудников
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Change own password button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setError('');
                  setShowChangePasswordDialog(true);
                }}
                className="flex items-center gap-2"
                data-testid="change-own-password-btn"
              >
                <Key className="w-4 h-4" />
                Сменить пароль
              </Button>
              
              {canCreate && (
                <Button
                  onClick={() => {
                    setError('');
                    setShowCreateDialog(true);
                  }}
                  className="flex items-center gap-2"
                  data-testid="add-employee-btn"
                >
                  <Plus className="w-4 h-4" />
                  Добавить сотрудника
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="system-users" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Пользователи системы
            </TabsTrigger>
            <TabsTrigger value="bitrix-employees" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Сотрудники Bitrix
            </TabsTrigger>
          </TabsList>

          {/* === TAB 1: Системные пользователи (4 аккаунта) === */}
          <TabsContent value="system-users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Пользователи системы
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Аккаунты для работы с системой. Роли: Finance Admin, Senior Manager
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold">ФИО</th>
                        <th className="text-left py-3 px-4 font-semibold">Email</th>
                        <th className="text-left py-3 px-4 font-semibold">Роль</th>
                        <th className="text-left py-3 px-4 font-semibold">Статус</th>
                        <th className="text-right py-3 px-4 font-semibold">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemUsers.map((sysUser) => {
                        const roleInfo = SYSTEM_USER_ROLE_OPTIONS.find(r => r.value === sysUser.role) || SYSTEM_USER_ROLE_OPTIONS[1];
                        return (
                          <tr key={sysUser.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{sysUser.full_name}</td>
                            <td className="py-3 px-4 text-gray-600">{sysUser.email}</td>
                            <td className="py-3 px-4">
                              {isAdmin ? (
                                <Select
                                  value={sysUser.role}
                                  onValueChange={(value) => handleSystemUserRoleChange(sysUser.id, value)}
                                  disabled={updatingRoleId === sysUser.id}
                                >
                                  <SelectTrigger className="w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SYSTEM_USER_ROLE_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {sysUser.is_active ? (
                                <Badge className="bg-green-100 text-green-800">Активен</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">Неактивен</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              {canResetPassword && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUser(sysUser);
                                    setError('');
                                    setShowResetPasswordDialog(true);
                                  }}
                                  title="Сбросить пароль"
                                >
                                  <Key className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Access Matrix Card - Interactive */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Доступ к страницам
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Нажмите на галочку/крестик для изменения доступа
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold">Страница</th>
                        {ALL_ROLES.map(role => (
                          <th key={role.value} className="text-center py-3 px-2 font-semibold">
                            <Badge className={`${role.color} text-xs`}>{role.label}</Badge>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageAccessMatrix.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4">
                            <span className="font-medium">{row.page}</span>
                            <span className="text-gray-400 text-xs ml-2">{row.path}</span>
                          </td>
                          {ALL_ROLES.map(role => {
                            const isUpdating = updatingAccess?.page === row.page && updatingAccess?.role === role.value;
                            const hasAccess = row[role.value];
                            return (
                              <td key={role.value} className="text-center py-2 px-2">
                                {isAdmin ? (
                                  <button
                                    onClick={() => handlePageAccessToggle(row.page, role.value)}
                                    disabled={isUpdating}
                                    className={`p-1 rounded transition-colors ${
                                      isUpdating ? 'opacity-50 cursor-wait' :
                                      hasAccess
                                        ? 'hover:bg-green-100 cursor-pointer'
                                        : 'hover:bg-red-100 cursor-pointer'
                                    }`}
                                    title={hasAccess ? 'Нажмите чтобы запретить' : 'Нажмите чтобы разрешить'}
                                  >
                                    {hasAccess ? (
                                      <Check className="w-5 h-5 text-green-600 mx-auto" />
                                    ) : (
                                      <X className="w-5 h-5 text-red-400 mx-auto" />
                                    )}
                                  </button>
                                ) : (
                                  hasAccess ? (
                                    <Check className="w-5 h-5 text-green-600 mx-auto" />
                                  ) : (
                                    <X className="w-5 h-5 text-red-400 mx-auto" />
                                  )
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {isAdmin && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Совет:</strong> Кликните на иконку ✓ или ✕ чтобы переключить доступ. Изменения сохраняются автоматически.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Role descriptions */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Описание ролей
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SYSTEM_USER_ROLE_OPTIONS.map(role => (
                    <div key={role.value} className={`p-4 rounded-lg border-2 ${role.color.replace('text-', 'border-').replace('-800', '-300')}`}>
                      <Badge className={`${role.color} mb-2`}>{role.label}</Badge>
                      <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                      <p className="text-xs text-gray-400">role: {role.value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB 2: Сотрудники Bitrix (для аналитики) === */}
          <TabsContent value="bitrix-employees">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Сотрудники Bitrix
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Синхронизированы из Bitrix24 для учёта и аналитики. Не влияют на авторизацию.
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="employees-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">ФИО</th>
                        <th className="text-left py-3 px-4 font-semibold">Должность</th>
                        <th className="text-left py-3 px-4 font-semibold">Email</th>
                        <th className="text-left py-3 px-4 font-semibold">Роль</th>
                        <th className="text-right py-3 px-4 font-semibold">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((employee) => {
                        const roleInfo = getRoleBadge(employee.role);
                        return (
                          <tr
                            key={employee.id}
                            className="border-b hover:bg-gray-50"
                            data-testid={`employee-row-${employee.id}`}
                          >
                            <td className="py-3 px-4 font-medium">{employee.full_name}</td>
                            <td className="py-3 px-4 text-gray-600">{employee.position}</td>
                            <td className="py-3 px-4 text-gray-600">{employee.email}</td>
                            <td className="py-3 px-4">
                              {canChangeRole ? (
                                <Select
                                  value={employee.role}
                                  onValueChange={(value) => handleRoleChange(employee.id, value)}
                                  disabled={updatingRoleId === employee.id}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {EMPLOYEE_ROLE_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge className={roleInfo.color}>
                                  {employee.role}
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {canDelete && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedEmployee(employee);
                                      setShowDeleteDialog(true);
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-employee-${employee.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Permissions info card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Ваша роль: {user?.role}</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  {isAdmin && (
                    <>
                      <li>Изменение ролей пользователей системы</li>
                      <li>Сброс паролей</li>
                    </>
                  )}
                  {userRole === 'senior_manager' && (
                    <>
                      <li>Просмотр пользователей и сотрудников</li>
                      <li>Нет доступа к Bitrix и Field Registry</li>
                    </>
                  )}
                  <li>Смена собственного пароля</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Employee Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-employee-dialog">
          <DialogHeader>
            <DialogTitle>Новый сотрудник</DialogTitle>
            <DialogDescription>
              Заполните данные нового сотрудника. После создания сбросьте пароль через панель.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">ФИО *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Иванов Иван Иванович"
                  data-testid="employee-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Должность</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Менеджер проектов"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@company.kz"
                  data-testid="employee-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Роль</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="employee-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_ROLE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" data-testid="submit-employee-btn">
                Создать
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить сотрудника?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить <strong>{selectedEmployee?.full_name}</strong>?
              <br />
              Это действие нельзя отменить. Также будет удалена учётная запись пользователя.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog - работает с selectedUser или selectedEmployee */}
      <Dialog open={showResetPasswordDialog} onOpenChange={(open) => {
        setShowResetPasswordDialog(open);
        if (!open) {
          setSelectedUser(null);
          setSelectedEmployee(null);
        }
      }}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Сброс пароля</DialogTitle>
            <DialogDescription>
              Установите новый пароль для <strong>{selectedUser?.full_name || selectedEmployee?.full_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Новый пароль</Label>
              <Input
                id="newPassword"
                type="password"
                value={resetPasswordData.newPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                placeholder="Минимум 12 символов"
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-500">Заглавная, строчная, цифра и спецсимвол (!@#$%...)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                placeholder="Повторите пароль"
                data-testid="confirm-password-input"
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetPasswordDialog(false);
              setResetPasswordData({ newPassword: '', confirmPassword: '' });
              setSelectedUser(null);
              setSelectedEmployee(null);
              setError('');
            }}>
              Отмена
            </Button>
            <Button onClick={handleResetPassword} data-testid="submit-reset-password-btn">
              Сбросить пароль
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Own Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Смена пароля</DialogTitle>
            <DialogDescription>
              Введите текущий и новый пароль
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Текущий пароль</Label>
              <Input
                id="currentPassword"
                type="password"
                value={changePasswordData.currentPassword}
                onChange={(e) => setChangePasswordData({ ...changePasswordData, currentPassword: e.target.value })}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword2">Новый пароль</Label>
              <Input
                id="newPassword2"
                type="password"
                value={changePasswordData.newPassword}
                onChange={(e) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
                placeholder="Минимум 12 символов"
                data-testid="new-password2-input"
              />
              <p className="text-xs text-gray-500">Заглавная, строчная, цифра и спецсимвол (!@#$%...)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword2">Подтвердите новый пароль</Label>
              <Input
                id="confirmPassword2"
                type="password"
                value={changePasswordData.confirmPassword}
                onChange={(e) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
                data-testid="confirm-password2-input"
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowChangePasswordDialog(false);
              setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setError('');
            }}>
              Отмена
            </Button>
            <Button onClick={handleChangeOwnPassword} data-testid="submit-change-password-btn">
              Сменить пароль
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesPage;
